const sectionsFinder = function (opts) {
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Regex for detecting exhibits, annexes, schedules, and index of defined terms
  const EXHIBIT_ANNEX_REGEX =
    /^(Exhibit|Annex|Schedule|Index of Defined Terms)\s*(?:[A-Z0-9]+)?/i;

  // Regex for detecting signature pages
  const SIGNATURE_PAGE_REGEX = /\[Signature page(?: follows)?\]/i;

  // Regex for detecting table of contents
  const TABLE_OF_CONTENTS_REGEX = /^table\s+of\s+contents/i;

  // Updated regex patterns to exclude percentage matches
  // const sectionRegex =
  //   /^(?:Section\s+)?\d+\.\d{1,2}(?!\s*\(|\s*,?\s*Article|\s*%|\d*%).*?(?=\.|$)/i;
  const articleRegex = /^Article\s+[A-Z0-9]+/i;

  /* const getEquivalentTerms = function (term) {
     return [term];
   };
 
   const initAlternativeTerms = function (word, paragraphs) {
     const equivalentTerms = getEquivalentTerms(word);
     return equivalentTerms.map(term => ({
       word: term,
       match: term,
       id: generateId()
     }));
   };*/

  const formatSectionTitle = (title, sectionNumber) => {
    if (!title) {
      return title;
    }

    let processedTitle = title;

    // Check if title contains "section" word (case insensitive)
    if (!/\bsection\b/i.test(processedTitle)) {
      processedTitle = `Section ${processedTitle}`;
    }

    // Check if title contains a number followed by a dot
    if (sectionNumber && !/\d+\./.test(processedTitle)) {
      // If the title starts with "Section" but doesn't have the number.dot pattern
      if (/^section\s+/i.test(processedTitle)) {
        processedTitle = processedTitle.replace(
          /^section\s+/i,
          `Section ${sectionNumber} `
        );
      } else {
        processedTitle = `${sectionNumber}. ${processedTitle}`;
      }
    }

    return processedTitle.replace(/\.$/, "");
  };

  const findSectionMatchInText = (text) => {
    // Return string empty if text is undefined or null
    if (!text) {
      return "";
    }

    // Updated regex patterns to handle the pattern " 6.3\n\n6.3. Return of..."
    // Also updated to support multi-level section numbers like 5.3.2, 4.5.6.7
    const contentRegex =
      /^(?:(?:Section\s+)?(\d+(?:\.\d+)+)(?!\s*\(|\s*,?\s*Article|\s*%|\d*%)\s*(.*?))(?=\.|$)/i;
    const sectionWithSectionAndSingleNumberRegex =
      /Section\s+(\d+)\.\s+([^\.]+)\./;
    // Fixed regex to properly handle section numbers followed by whitespace, newlines, or end of string
    // Also handles "Section X.Y" patterns for consistency with back-end
    // Updated to support multi-level section numbers like 5.3.2, 4.5.6.7
    const sectionNumberRegex =
      /^\s*(?:Section\s+)?(\d+(?:\.\d+)+)(?=\s|\n|$|\s*[A-Z]|\s*\.)/i;

    let sectionMatch = text.match(contentRegex);
    if (!sectionMatch) {
      sectionMatch = text.match(sectionWithSectionAndSingleNumberRegex);
      if (!sectionMatch) {
        sectionMatch = text.match(sectionNumberRegex);
        if (!sectionMatch) {
          // Fallback regex to match simple section numbers like "31.", "1." (with required dot)
          const simpleSectionRegex =
            /^\s*(?:Section\s+)?(\d+)\.(?=\s|\n|$|\s*[A-Z])/i;
          sectionMatch = text.match(simpleSectionRegex);
          if (!sectionMatch) {
            return "";
          }
        }
      }
    }
    return sectionMatch;
  };

  const findSections = (groupedParagraphs) => {
    const sections = [];

    groupedParagraphs.forEach((group, index) => {
      // Extract section number from the group name
      const sectionMatch = findSectionMatchInText(group.name);
      if (sectionMatch) {
        const sectionNumber = sectionMatch[1];
        const sectionTitle = formatSectionTitle(group.name, sectionNumber);
        const word = sectionTitle;
        // Check if any paragraphs in this group are from TOC
        const hasTocParagraphs = group.paragraphs.some((para) => para.isToc);

        // Only process if we have TOC paragraphs or if we want to include all sections
        if (hasTocParagraphs || group.paragraphs.length > 0) {
          // Collect defined definitions from the paragraphs
          const definedDefinition = [];

          group.paragraphs.forEach((para) => {
            const cleanText = para.text
              ? para.text.trim().replace(/\s+\d+$/, "")
              : "";

            // Add paragraphs that are sections but not the main title
            if (
              para.isParagraph &&
              para.isSection &&
              cleanText !== word &&
              cleanText !== sectionTitle
            ) {
              definedDefinition.push({
                html: para.html,
                text: para.text,
                isParagraph: para.isParagraph,
              });
            }
          });

          const section = {
            id: generateId(),
            match: [sectionTitle, word, sectionNumber],
            word: word,
            definition: null,
            definedDefinition: definedDefinition,
            content: [],
            definedTableDetected: false,
            title: sectionTitle,
            alternativeTerms: [],
            type: "section",
            sectionNumber: sectionNumber,
          };
          sections.push(section);
        }
      }
    });

    return sections;
  };

  const findSectionsWithoutToc = (groupedParagraphs) => {
    const sections = [];

    groupedParagraphs.forEach((group) => {
      const sectionMatch = findSectionMatchInText(group.name);
      if (sectionMatch) {
        const sectionNumber = sectionMatch[1];
        const sectionTitle = formatSectionTitle(group.name, sectionNumber);
        const word = sectionTitle;
        // Check if any paragraphs contain signature page or exhibit content
        const hasSignatureOrExhibit = group.paragraphs.some(
          (para) =>
            para.text &&
            (para.text.toLowerCase().match(SIGNATURE_PAGE_REGEX) ||
              para.text.match(EXHIBIT_ANNEX_REGEX))
        );

        // Skip this group if it contains signature page or exhibit content
        if (hasSignatureOrExhibit) {
          return;
        }

        // Collect defined definitions from the paragraphs
        const definedDefinition = [];

        group.paragraphs.forEach((para) => {
          if (!para.text) {
            return;
          }

          const cleanText = para.text.trim().replace(/\s+\d+$/, "");

          // Add paragraphs that are sections but not the main title
          if (
            para.isParagraph &&
            para.isSection &&
            cleanText !== word &&
            cleanText !== sectionTitle
          ) {
            definedDefinition.push({
              html: para.html,
              text: para.text,
              isParagraph: para.isParagraph,
            });
          }
        });

        const section = {
          id: generateId(),
          match: [sectionTitle, word, sectionNumber],
          word: word,
          definition: null,
          definedDefinition: definedDefinition,
          content: [],
          definedTableDetected: false,
          title: sectionTitle,
          alternativeTerms: [],
          type: "section",
          sectionNumber: sectionNumber,
        };

        sections.push(section);
      }
    });

    return sections;
  };

  // Helper function to find the next section starting from a given index
  const findNextSectionIndex = (paragraphs, startIndex) => {
    for (let i = startIndex; i < paragraphs.length; i++) {
      const para = paragraphs[i];

      // Skip if we hit signature page or exhibits
      if (
        para.text.toLowerCase().match(SIGNATURE_PAGE_REGEX) ||
        para.text.match(EXHIBIT_ANNEX_REGEX)
      ) {
        return i; // Return this index as the boundary
      }

      // Check if this paragraph is a section header
      const sectionMatch = findSectionMatchInText(para.text);
      if (sectionMatch) {
        return i;
      }
    }
    return paragraphs.length; // Return end of array if no next section found
  };

  // New function to group content between section headers
  const groupSectionContent = (paragraphs) => {
    let sectionGroups = new Map();

    // First pass: identify all sections and their positions
    const sectionPositions = [];
    paragraphs.forEach((para, index) => {
      // Check if this paragraph is a section header
      const sectionMatch = findSectionMatchInText(para.text);
      if (sectionMatch) {
        sectionPositions.push({
          index: index,
          sectionMatch: sectionMatch,
          paragraph: para,
        });
      }
    });

    // Second pass: process each section and collect all paragraphs until the next section
    sectionPositions.forEach((sectionPos, sectionIndex) => {
      const { index, sectionMatch, paragraph } = sectionPos;

      // Create the section name - preserve original format with dots
      const sectionNumber = sectionMatch[1];
      // Use the original matched text but clean it up
      let sectionTitle = sectionMatch[0];
      // Remove any trailing content after the section title (like definitions)
      sectionTitle = sectionTitle.replace(/\.\s*The\s+.*$/, ".").trim();
      // If it doesn't end with a period, add one for consistency
      if (!sectionTitle.endsWith(".")) {
        sectionTitle += ".";
      }

      // Find the next section index to determine the boundary
      const nextSectionIndex =
        sectionIndex < sectionPositions.length - 1
          ? sectionPositions[sectionIndex + 1].index
          : findNextSectionIndex(paragraphs, index + 1);

      // Collect all paragraphs between this section and the next section
      const sectionParagraphs = [];

      // Check if the current section paragraph contains content after the header
      const sectionHeaderText = sectionMatch[0];
      const remainingText = paragraph.text
        ? paragraph.text.replace(sectionHeaderText, "").trim()
        : "";

      if (remainingText) {
        // Only if there's substantial content
        const contentPara = {
          html: paragraph.html,
          text: remainingText,
          isParagraph: paragraph.isParagraph,
          tableNestingLevel: paragraph.tableNestingLevel,
          isToc: paragraph.isToc,
          isSection: paragraph.isSection,
        };
        sectionParagraphs.push(contentPara);
      }

      // Collect all paragraphs between current section and next section
      for (let i = index; i < nextSectionIndex; i++) {
        const para = paragraphs[i];
        const cleanText = para.text ? para.text.trim() : "";

        // Only skip if it's empty, explicitly TOC, signature page, or exhibit content
        if (
          cleanText &&
          !cleanText.toLowerCase().match(TABLE_OF_CONTENTS_REGEX) &&
          !cleanText.toLowerCase().match(SIGNATURE_PAGE_REGEX) &&
          !cleanText.match(EXHIBIT_ANNEX_REGEX)
        ) {
          sectionParagraphs.push(para);
        }
      }

      // Group sections with same number or name
      if (sectionGroups.has(sectionTitle)) {
        // If section already exists, append paragraphs to existing group
        sectionGroups.get(sectionTitle).paragraphs.push(...sectionParagraphs);
      } else {
        // Create new section group
        sectionGroups.set(sectionTitle, {
          name: sectionTitle,
          paragraphs: sectionParagraphs,
        });
      }
    });

    // Convert Map to array and return
    return Array.from(sectionGroups.values());
  };

  return {
    getSections: function (dom) {
      let isToc = false;
      let isSection = false;
      let isInArticle = false; // Add this flag at the start of getSections

      const paragraphs = Array.from(
        dom.querySelectorAll("p, tr, h1, h2, h3, h4, h5, h6")
      )
        .filter((item) => item.textContent?.trim())
        .map((item) => {
          const text = item.textContent?.replace(/\s+/g, " ").trim() || "";

          // Track if we're in a section and not under an article
          if (!isToc) {
            const sectionMatch = findSectionMatchInText(text);
            const articleMatch = text.match(articleRegex);

            if (sectionMatch) {
              isInArticle = false; // Reset article flag when we hit a new section
              isSection = true;
            } else if (articleMatch) {
              isInArticle = true; // Set article flag when we hit an article
              isSection = false;
            } else {
              // Content inherits previous isSection state, but only if not in an article
              isSection = !isInArticle;
            }
          }

          if (text && text.toLowerCase().match(TABLE_OF_CONTENTS_REGEX)) {
            isToc = true;
          } else if (text && text.match(EXHIBIT_ANNEX_REGEX)) {
            isToc = false;
          }

          return {
            html: item.outerHTML?.replace(/&nbsp;/g, " ").trim() || "",
            text,
            isParagraph: ["p", "h1", "h2", "h3", "h4", "h5", "h6"].includes(
              item.localName
            ),
            tableNestingLevel: item.closest("table") ? 1 : 0,
            isToc,
            isSection,
          };
        });

      // Use the new grouping logic that combines content between section headers
      const groupedSectionsParagraphs = groupSectionContent(paragraphs);
      const sectionsWithToc = findSections(groupedSectionsParagraphs);
      return sectionsWithToc.length > 0
        ? sectionsWithToc
        : findSectionsWithoutToc(groupedSectionsParagraphs);
    },
  };
};

export default sectionsFinder;
